import React, { useEffect, useState } from "react";
import { globalViewStyles } from "./GlobalViewStyles";
import { GlobalViewTableWrapper } from "./GlobalViewTableWrapper";
import { observer } from "mobx-react";
import { useStore } from "store/StoreConfigs";
import clsx from "clsx";
import { dateEndFormatting, dateStartFormatting } from "helpers/dateFormatting";
import { GlobalViewTotalsWidgetsWrapper } from "./GlobalViewTotalsWidgetsWrapper";
import AdvancedFilters from "components/AdvancedFilters/AdvancedFilters";
import { GlobalViewMap } from "components/GlobalViewMap/GlobalViewMap";
import { LanesDetailsRequestModel } from "api/lokiRequestModels/lane/LanesDetailsRequestModel";
import DynamicGridTitle from "components/GridTitle/DynamicGridTitle";
import { ShipmentDetailsRequestModel } from "api/lokiRequestModels/shipments/ShipmentDetailsRequestModel";
import { GlobalViewShipmentsTableWrapper } from "./GlobalViewShipmentsTableWrapper";

export default observer(function GlobalView() {
    const classes = globalViewStyles();
    const { globalViewStore, filtersStore, identityStore, commonStore } = useStore();
    const [isFiltersReset, setResetFilters] = useState(false);

    const laneRequest: LanesDetailsRequestModel = {
        // TODO: move these into filters store to reduce duplication
        startTime: dateStartFormatting(filtersStore.startDate),
        endTime: dateEndFormatting(filtersStore.endDate),
        customerId: identityStore.currentCustomerId,
        lsp: filtersStore.selectedLogisticsServiceProviders.join(",") || undefined,
        productType: filtersStore.selectedProductTypes.join(",") || undefined,
        riskLevel: filtersStore.selectRiskLevels.join(",") || undefined,
        transportationModes: filtersStore.selectedModesOfTransportation.join(",") || undefined,
        packagingType: filtersStore.selectedPackagingTypes.join(",") || undefined,
        sort: globalViewStore.laneSort,
        direction: globalViewStore.laneDirection,
    };

    const shipmentRequest: ShipmentDetailsRequestModel = {
        // TODO: move these into filters store to reduce duplication
        startTime: dateStartFormatting(filtersStore.startDate),
        endTime: dateEndFormatting(filtersStore.endDate),
        customerId: identityStore.currentCustomerId,
        laneId: "",
        lsp: filtersStore.selectedLogisticsServiceProviders.join(",") || undefined,
        productType: filtersStore.selectedProductTypes.join(",") || undefined,
        riskLevel: filtersStore.selectRiskLevels.join(",") || undefined,
        transportationModes: filtersStore.selectedModesOfTransportation.join(",") || undefined,
        packagingType: filtersStore.selectedPackagingTypes.join(",") || undefined,
        sort: globalViewStore.shipmentSort,
        direction: globalViewStore.shipmentDirection,
    };

    const callbackRequest = () => {
        if (filtersStore.startDate === null || filtersStore.endDate === null) {
            return;
        }

        globalViewStore.loadAggregations(laneRequest);
        globalViewStore.loadTemperatureExcursions(laneRequest);
        globalViewStore.loadMapDetails(laneRequest);
        commonStore.setFiltersClose();
    };

    const AdvancedFiltersRequest = () => {
        if (filtersStore.startDate === null || filtersStore.endDate === null) {
            return;
        }

        globalViewStore.loadAggregations(laneRequest);
        globalViewStore.loadTemperatureExcursions(laneRequest);
        globalViewStore.loadMapDetails(laneRequest);
        globalViewStore.loadFirstLanePage(laneRequest);
        globalViewStore.loadFirstShipmentPage(shipmentRequest);
        commonStore.setFiltersClose();
    };
    useEffect(() => {
        isFiltersReset ? setResetFilters(true) : filtersStore.resetFilters();

        callbackRequest();
        window.scrollTo(0, 0);
    }, [filtersStore.startDate, filtersStore.endDate, identityStore.currentCustomerId]);

    useEffect(() => {
        if (filtersStore.startDate === null || filtersStore.endDate === null) {
            return;
        }
        globalViewStore.loadFirstLanePage(laneRequest);
        commonStore.setFiltersClose();
    }, [globalViewStore.laneSort, globalViewStore.laneDirection, filtersStore.startDate, filtersStore.endDate, identityStore.currentCustomerId]);

    useEffect(() => {
        if (filtersStore.startDate === null || filtersStore.endDate === null) {
            return;
        }
        globalViewStore.loadFirstShipmentPage(shipmentRequest);
        commonStore.setFiltersClose();
    }, [globalViewStore.shipmentSort, globalViewStore.shipmentDirection, filtersStore.startDate, filtersStore.endDate, identityStore.currentCustomerId]);

    useEffect(() => {
        commonStore.setCurrentView("global");
    }, []);

    return (
        <>
            <AdvancedFilters requestCallback={AdvancedFiltersRequest} />

            <div
                className={clsx({
                    [classes.dis]: commonStore.isFilterOpen,
                })}
            ></div>

            <main className={classes.content}>
                <div className={classes.toolbar} />

                <div className={classes.section}>
                    <GlobalViewMap />
                </div>

                <GlobalViewTotalsWidgetsWrapper />

                <div className={classes.section}>
                    <DynamicGridTitle
                        firstOnClick={commonStore.setLanesSelected}
                        secondOnClick={commonStore.setShipmentsSelected}
                        firstStyle={commonStore.isLaneSelected}
                        secondStyle={commonStore.isShipmentsSelected}
                        firstTitle={"Lanes"}
                        secondTitle={"Shipments"}
                        subText={"Survey the latest lane risk level changes."}
                    />

                    {commonStore.isLaneSelected && <GlobalViewTableWrapper />}
                    {commonStore.isShipmentsSelected && <GlobalViewShipmentsTableWrapper />}
                </div>
            </main>
        </>
    );
});
